import NoStockButton from "~/components/product/VariantSelector/NoStockButton"
import VariantLink, { useVariantNavigate } from "~/components/product/VariantSelector/VariantLink"
import Button from "~/components/ui/Button"
import { selectedProducts, useItemsCount, useSelectedProducts, useSubtotal } from "~/sdk/atoms"
import clsx from "~/sdk/clx"
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice"
import { useSignalEffect } from "~/sdk/signal"
import { AggregateOffer, ImageObject, ProductLeaf, PropertyValue } from "@decocms/apps-commerce/types"
import Image from "~/components/ui/Image"
import { useOffer } from "~/sdk/useOffer"

export interface Props {
    hasVariant: ProductLeaf[]
    selector: string
    reverse?: boolean
    isCurrentProductAvailable: boolean
}

interface VariantProperty {
    name: string
    value: string
    isAvailable: boolean
    productID: string
    quantity: number
    url?: string
}

interface Variant {
    name: string | undefined
    properties: VariantProperty[]
    image: ImageObject
}

function isSpecification(property: PropertyValue) {
    return property.valueReference === 'SPECIFICATION'
}

type MapPropertyProps = {
    isAvailable: boolean
    productID: string
    quantity: number
    url?: string
}

function mapProperty({ isAvailable, productID, url, quantity }: MapPropertyProps) {
    return (
        { name = 'Unknown Name', value = 'Unknown Value' }: PropertyValue,
    ): VariantProperty => ({
        name,
        value,
        isAvailable,
        productID,
        quantity,
        url,
    })
}

function isAvailable(offers: AggregateOffer) {
    return offers.offers[0]?.availability === 'https://schema.org/InStock'
}

function useVariant(leafs: ProductLeaf[], selector: string) {
    return (
        leafs
            .map(
                ({ name, additionalProperty = [], image: images = [], offers, productID, url }) => {
                    return {
                        name,
                        properties: additionalProperty.filter(isSpecification).map(
                            mapProperty({
                                isAvailable: (offers && isAvailable(offers)) ?? false,
                                productID,
                                url,
                                quantity: offers?.offers[0]?.inventoryLevel.value ?? 0,
                            }),
                        ),
                        images,
                    }
                },
            )
            .reduce((acc, { name, properties, images = [] }) => {
                const color = properties.find((p) => p.name === selector)?.value

                const index = acc.findIndex((a) =>
                    a.properties.some((p) => p.name === selector && p.value === color)
                )

                if (index === -1) {
                    acc.push({
                        name,
                        properties,
                        image: images?.[0] ?? { '@type': 'ImageObject' },
                    })
                } else {
                    acc[index].properties.push(...properties.filter((p) => p.name !== selector))
                }

                return acc
            }, [] as Variant[])
            // Remove duplicates in properties, because the client created equal variants in some products
            .reduce((acc, cur) => {
                for (const p of cur.properties.slice()) {
                    const dups = cur.properties.reduce((acc, cur, i) => {
                        if (cur.name === p.name && cur.value === p.value) {
                            acc.push(i)
                        }
                        return acc
                    }, [] as number[])

                    if (dups.length > 1) {
                        const first = dups[0]

                        for (const i of dups.slice(1)) {
                            cur.properties.splice(i, 1, cur.properties[first])
                        }

                        cur.properties.splice(first, 1)
                    }
                }

                return [...acc, cur]
            }, [] as Variant[])
    )
}

const SIZES_USED_TO_SORT_SKUS_SIZES_IN_GRID_OF_PDP = [
    'P',
    'M',
    'G',
    'GG',
    'XGG',
    'EG1',
    'EG2',
    'EG3',
    'EG4',
]

export default function (
    { hasVariant, selector, reverse, isCurrentProductAvailable }: Props,
) {
    // Válvula de escape do estado esgotado: quando a SKU da URL não tem estoque,
    // o buy box renderiza o formulário "avise-me" no lugar do CTA de compra
    // (InfosAndBuyButton). Mexer na quantidade de um tamanho DISPONÍVEL precisa
    // então trazer a página para aquela SKU, senão o cliente escolhe quantidade
    // e não tem botão para comprar. No Fresh era o `f-partial` condicional destes
    // mesmos botões; aqui é a navegação SPA equivalente.
    const navigateToVariant = useVariantNavigate()
    const restoreBuyBox = (url?: string) => {
        if (!isCurrentProductAvailable) void navigateToVariant(url)
    }
    // Subscribe to the selectedProducts atom for reactivity on +/- clicks.
    // See atoms.ts comment — `signal.value` reads in render are NOT reactive
    // without useStore.
    const selected = useSelectedProducts()
    const itemsCountValue = useItemsCount()
    const subtotalValue = useSubtotal()
    // Derived from props on every render — do NOT park this in useSignal/useState:
    // the initial value would freeze on mount and an SPA navigation between PDPs
    // (same route, component instance reused) would keep rendering the previous
    // product's variants.
    const variants = useVariant(hasVariant, selector)

    // This is a hack to reset the selected products when the user go
    // back to the product page. This is needed because  some inputs
    // may be filled with the wrong quantity.
    useSignalEffect(() => {
        selectedProducts.value = new Map()
    })

    function updateProductQuantity(key: string, quantity: number) {
        if (quantity === 0) {
            selectedProducts.value.delete(key)
            selectedProducts.value = new Map(selectedProducts.value)
            return
        }

        const selectedProduct = selectedProducts.value.get(key)

        if (selectedProduct) {
            selectedProducts.value.set(key, { ...selectedProduct, quantity })
        } else {
            const product = hasVariant.find((v) => v.productID === key)

            if (!product) {
                return
            }

            const { price = 0, listPrice = 0 } = useOffer(product.offers)
            const discount = listPrice - price

            selectedProducts.value.set(key, { ...product, price, quantity, discount })
        }

        selectedProducts.value = new Map(selectedProducts.value)
    }

    const headers = new Set<string>()

    headers.add(
        variants[0].properties.find((p) => {
            if (reverse) {
                return p.name === selector
            }

            return p.name !== selector
        })!.name,
    )

    for (const variant of variants) {
        for (
            const property of variant.properties.sort((a, b) => {
                return (
                    SIZES_USED_TO_SORT_SKUS_SIZES_IN_GRID_OF_PDP.indexOf(a.value) -
                    SIZES_USED_TO_SORT_SKUS_SIZES_IN_GRID_OF_PDP.indexOf(b.value)
                )
            })
        ) {
            if (reverse) {
                if (property.name !== selector) {
                    headers.add(property.value)
                } else {
                    headers.add(property.name)
                }
            } else {
                if (property.name === selector) {
                    headers.add(property.value)
                } else {
                    headers.add(property.name)
                }
            }
        }
    }

    const body = new Map<string, Variant & { value?: string }>()

    if (reverse) {
        for (const variant of variants) {
            const first = variant.properties.find((p) => {
                return p.name === selector
            })!

            const others = variant.properties.filter((p) => {
                return p.name !== selector
            })

            body.set(`${first.name}-${first.value}`, {
                name: variant.name,
                image: variant.image,
                properties: others.sort((a, b) => {
                    return (
                        SIZES_USED_TO_SORT_SKUS_SIZES_IN_GRID_OF_PDP.indexOf(a.value) -
                        SIZES_USED_TO_SORT_SKUS_SIZES_IN_GRID_OF_PDP.indexOf(b.value)
                    )
                }),
                value: first.value,
            })
        }
    } else {
        for (const variant of variants) {
            const others = variant.properties.filter((p) => {
                return p.name !== selector
            })

            for (const i of others) {
                const value = i.value
                const name = i.name

                const properties = [] as VariantProperty[]

                for (const v of variants) {
                    const p = v.properties.find((p) => {
                        return `${p.name}-${p.value}` === `${name}-${value}`
                    })

                    if (p) {
                        properties.push(p)
                    }
                }

                body.set(`${name}-${value}`, {
                    name,
                    image: variant.image,
                    properties: properties.sort((a, b) => {
                        return (
                            SIZES_USED_TO_SORT_SKUS_SIZES_IN_GRID_OF_PDP.indexOf(a.value) -
                            SIZES_USED_TO_SORT_SKUS_SIZES_IN_GRID_OF_PDP.indexOf(b.value)
                        )
                    }),
                })
            }
        }
    }

    return (
        <>
            <p className='text-sm max-lg:mt-2'>Selecione os itens desejados</p>
            <div className='w-full max-w-fit max-h-[400px] mt-[9px] scroll scroll-xl scroll-salmon overflow-y-auto max-lg:-order-1 isolate'>
                <ul className='text-sm border relative border-neutral-300 min-w-fit'>
                    {/* z-10: the thumbnail cells below are `relative` (VariantLink), and a
                        positioned sibling later in tree order would otherwise paint over this
                        sticky header while scrolling. */}
                    <li className='sticky top-0 z-10'>
                        <div className='font-black gap-1 flex bg-pink-light w-full text-center border-b border-b-[#ababab]'>
                            {[...headers].map((name, i) => <p key={`${name}-${i}`} className='uppercase leading-[26px] w-20'>{name}</p>)}
                        </div>
                    </li>
                    {/* Sort by less unavailable products */}
                    {[...body.values()]
                        .sort(
                            (a, b) =>
                                a.properties.filter((p) => !p.isAvailable).length -
                                b.properties.filter((p) => !p.isAvailable).length,
                        )
                        .map(({ image, name: _name, properties, value }) => {
                            const url = hasVariant.find(({ additionalProperty }) => {
                                return additionalProperty?.some((p) => {
                                    return p.value === value
                                })
                            })?.url

                            return (
                                <li className='flex gap-1 border-b border-neutral-300 pt-2.5 px-1'>
                                    <VariantLink
                                        url={url}
                                        className='flex flex-col items-center w-20 text-center py-0.5 pt-3 lg:pt-0.5 cursor-pointer hover:bg-neutral-200 transition-all'
                                    >
                                        {image.url ? (
                                            <Image
                                                className='border border-neutral-300 bg-white h-[76px] w-[76px] object-contain'
                                                width={76}
                                                height={76}
                                                src={image.url}
                                                alt=''
                                                loading='lazy'
                                                fit='contain'
                                            />
                                        ) : (
                                            <div className='border border-neutral-300 bg-white h-[76px] w-[76px]' />
                                        )}
                                        <p className='text-xs line-clamp-2 leading-none text-black mt-0.5'>
                                            {reverse ? value : properties.find((p) => {
                                                if (reverse) {
                                                    return p.name === selector
                                                }

                                                return p.name !== selector
                                            })?.value ?? null}
                                        </p>
                                    </VariantLink>
                                    {properties.map(
                                        (
                                            { isAvailable, name: _name, productID, url, quantity },
                                        ) => {
                                            return (
                                                <div className='py-0.5 pt-3 lg:pt-0.5 w-20'>
                                                    {isAvailable
                                                        ? (
                                                            <div>
                                                                <div className='flex justify-center'>
                                                                    <input
                                                                        id={productID}
                                                                        className={clsx(
                                                                            (selected
                                                                                    .get(productID)
                                                                                    ?.quantity ??
                                                                                    0) > 0
                                                                                ? 'bg-[#e9ffec]'
                                                                                : 'bg-neutral-200',
                                                                            'outline-none w-[62px] text-center h-11 rounded-t border border-b-0 border-neutral-300 text-lg',
                                                                        )}
                                                                        type='number'
                                                                        min={0}
                                                                        max={selected
                                                                            .get(
                                                                                productID,
                                                                            )?.offers?.offers[0]
                                                                            .inventoryLevel.value}
                                                                        aria-label={(selected
                                                                            .get(productID)
                                                                            ?.quantity ?? 0)
                                                                            .toString()}
                                                                        value={selected
                                                                            .get(productID)
                                                                            ?.quantity ?? 0}
                                                                        onChange={(e) => {
                                                                            const value = Math.min(
                                                                                selectedProducts
                                                                                    .value
                                                                                    .get(
                                                                                        productID,
                                                                                    )?.offers
                                                                                    ?.offers[0]
                                                                                    .inventoryLevel
                                                                                    .value ?? 100,
                                                                                Math.max(
                                                                                    0,
                                                                                    e.currentTarget
                                                                                        .valueAsNumber,
                                                                                ),
                                                                            )
                                                                            e.currentTarget.value =
                                                                                value.toString()

                                                                            updateProductQuantity(
                                                                                productID,
                                                                                value,
                                                                            )
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className='flex justify-center'>
                                                                    <Button
                                                                        onClick={() => {
                                                                            const input = document
                                                                                .getElementById(
                                                                                    productID,
                                                                                ) as HTMLInputElement
                                                                            const value = Math.min(
                                                                                selectedProducts
                                                                                    .value
                                                                                    .get(
                                                                                        productID,
                                                                                    )?.offers
                                                                                    ?.offers[0]
                                                                                    .inventoryLevel
                                                                                    .value ?? 100,
                                                                                Math.max(
                                                                                    0,
                                                                                    input
                                                                                        .valueAsNumber -
                                                                                        1,
                                                                                ),
                                                                            )
                                                                            input.value = value
                                                                                .toString()
                                                                            updateProductQuantity(
                                                                                productID,
                                                                                value,
                                                                            )
                                                                            restoreBuyBox(url)
                                                                        }}
                                                                        className='btn-xs text-xl bg-white border border-r-0 rounded-none border-neutral-300 h-[31px] w-[31px]'
                                                                    >
                                                                        -
                                                                    </Button>
                                                                    <Button
                                                                        onClick={() => {
                                                                            const input = document
                                                                                .getElementById(
                                                                                    productID,
                                                                                ) as HTMLInputElement
                                                                            const value = Math.min(
                                                                                selectedProducts
                                                                                    .value
                                                                                    .get(
                                                                                        productID,
                                                                                    )?.offers
                                                                                    ?.offers[0]
                                                                                    .inventoryLevel
                                                                                    .value ?? 100,
                                                                                Math.max(
                                                                                    0,
                                                                                    input
                                                                                        .valueAsNumber +
                                                                                        1,
                                                                                ),
                                                                            )
                                                                            input.value = value
                                                                                .toString()
                                                                            updateProductQuantity(
                                                                                productID,
                                                                                value,
                                                                            )
                                                                            restoreBuyBox(url)
                                                                        }}
                                                                        className='btn-xs text-xl bg-white border rounded-none border-neutral-300 h-[31px] w-[31px]'
                                                                    >
                                                                        +
                                                                    </Button>
                                                                </div>
                                                                <div className='h-7'>
                                                                    {quantity < 10 && (
                                                                        <>
                                                                            {quantity === 1
                                                                                ? (
                                                                                    <p className='text-xs text-red-500 text-center font-bold leading-[1.1]'>
                                                                                        Última
                                                                                        unidade
                                                                                    </p>
                                                                                )
                                                                                : (
                                                                                    <>
                                                                                        <p className='text-xs text-red-500 text-center font-bold leading-[1.1]'>
                                                                                            Restam
                                                                                        </p>
                                                                                        <p className='text-xs text-red-500 text-center font-bold leading-[1.1]'>
                                                                                            {quantity}
                                                                                            {' '}
                                                                                            uni 🔥
                                                                                        </p>
                                                                                    </>
                                                                                )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                        : <NoStockButton url={url} />}
                                                </div>
                                            )
                                        },
                                    )}
                                </li>
                            )
                        })}
                </ul>
            </div>
            <div className='my-2 bg-pink-light text-lg max-w-[300px] border-l-4 px-[14px] py-[10px] space-y-[5px] border-salmon text-salmon order-1 lg:order-[unset]'>
                <p>
                    <span className='font-bold'>Itens selecionados:</span> {itemsCountValue}
                </p>
                <p>
                    <span className='font-bold'>Subtotal:</span> {formatPrice(subtotalValue)}
                </p>
            </div>
        </>
    )
}
